/**
 * definition — the agent graph as the editor holds it. Version 2:
 *
 *   { version: 2, nodes: [{ id, kind, name, config }], edges: [{ from, to, port }] }
 *
 * This is the single source of truth in the editor. What React Flow draws is
 * derived from it every render — positions come from `layoutGraph`, never from
 * the definition — so the canvas can never disagree with what is saved, and
 * dragging a node is not a thing that happens to the data.
 *
 * Every function here is pure and returns a new definition.
 */

import { NODE_CATALOG, portsOf } from 'features/agents/WorkflowEditor/nodeCatalog';

export const VERSION = 2;

export function emptyDefinition() {
  return { version: VERSION, nodes: [], edges: [] };
}

export function isTriggerKind(kind) {
  return NODE_CATALOG[kind]?.category === 'trigger';
}

/** Whatever the server returned → a v2 definition the editor can hold. */
export function normalize(raw) {
  if (!raw || typeof raw !== 'object') return emptyDefinition();
  const nodes = (Array.isArray(raw.nodes) ? raw.nodes : [])
    .map((n) => (n && n.data && typeof n.data === 'object' && 'kind' in n.data
      // A v1 node the server has not converted yet (it converts on save).
      ? { id: String(n.id), kind: n.data.kind, name: n.data.name || n.data.kind, config: n.data.config || {} }
      : n))
    .filter((n) => n && n.id != null && n.kind)
    .map((n) => ({ id: String(n.id), kind: String(n.kind), name: String(n.name || NODE_CATALOG[n.kind]?.displayName || n.kind), config: n.config && typeof n.config === 'object' ? n.config : {} }));
  const ids = new Set(nodes.map((n) => n.id));
  const kindById = new Map(nodes.map((n) => [n.id, n.kind]));
  // A v1 edge: a Decide node's handle is its route id; anything else keeps only If's true / false.
  const v1Port = (e) => (kindById.get(String(e.source)) === 'decide'
    ? (e.sourceHandle ? String(e.sourceHandle) : null)
    : (e.sourceHandle === 'true' || e.sourceHandle === 'false' ? e.sourceHandle : null));
  const edges = (Array.isArray(raw.edges) ? raw.edges : [])
    .map((e) => (e && ('source' in e || 'target' in e)
      ? { from: e.source, to: e.target, port: v1Port(e) }
      : e))
    .filter((e) => e && ids.has(String(e.from)) && ids.has(String(e.to)))
    .map((e) => ({ from: String(e.from), to: String(e.to), port: e.port == null || e.port === '' ? null : String(e.port) }));
  return { version: VERSION, nodes, edges };
}

export function triggerOf(def) {
  return (def?.nodes || []).find((n) => isTriggerKind(n.kind)) || null;
}

export function nodeById(def, id) {
  return (def?.nodes || []).find((n) => n.id === id) || null;
}

export function newId() {
  return `n_${Math.random().toString(36).slice(2, 9)}`;
}

/** A node of `kind`, named and configured from the catalog. */
export function makeNode(kind, overrides = {}) {
  const spec = NODE_CATALOG[kind] || {};
  const defaults = typeof spec.defaults === 'function' ? spec.defaults() : (spec.defaults || {});
  return {
    id: newId(),
    kind,
    name: spec.displayName || kind,
    config: JSON.parse(JSON.stringify(defaults)),
    ...overrides,
  };
}

/**
 * Add a node. `after` names the node it follows; the new node is wired in
 * below it, taking over its outgoing connections when `insert` is set (the
 * "insert a step here" case) or simply hanging off it otherwise.
 */
export function addNode(def, node, { after = null, port = null, insert = false } = {}) {
  const nodes = [...def.nodes, node];
  let edges = [...def.edges];
  if (after && !isTriggerKind(node.kind)) {
    if (insert) {
      const out = outPortOf(node);
      edges = edges.map((e) => (e.from === after && (e.port ?? null) === (port ?? null) ? { ...e, from: node.id, port: out } : e));
    }
    edges.push({ from: after, to: node.id, port: port ?? null });
  }
  return { ...def, nodes, edges };
}

export function removeNode(def, id) {
  return {
    ...def,
    nodes: def.nodes.filter((n) => n.id !== id),
    edges: def.edges.filter((e) => e.from !== id && e.to !== id),
  };
}

/**
 * The port a node's single onward connection leaves by when a step is put in
 * front of what followed: its first port (If: true; Decide: its first route,
 * else Otherwise), or null for a node with one plain way out.
 */
export function outPortOf(node) {
  const ports = portsOf(node);
  return ports && ports.length ? ports[0] : null;
}

/**
 * Drop the edges that leave `id` by a port it no longer has: a Decide route
 * that was deleted, or Otherwise turned off. Saving never sends an edge the
 * server would refuse, and a route's NAME is not its id, so renaming one
 * keeps its connections.
 */
export function prunePorts(def, id) {
  const node = nodeById(def, id);
  // Only ports a node's own config names can disappear under it.
  if (!node || typeof NODE_CATALOG[node.kind]?.portsOf !== 'function') return def;
  const ports = portsOf(node) || [];
  const edges = def.edges.filter((e) => e.from !== id || (e.port != null && ports.includes(e.port)));
  return edges.length === def.edges.length ? def : { ...def, edges };
}

export function updateNode(def, id, patch) {
  return prunePorts({ ...def, nodes: def.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)) }, id);
}

export function updateNodeConfig(def, id, configPatch) {
  return prunePorts({ ...def, nodes: def.nodes.map((n) => (n.id === id ? { ...n, config: { ...(n.config || {}), ...configPatch } } : n)) }, id);
}

/** Connect two nodes. Refuses a duplicate, a self-edge, and anything into the trigger. */
export function connect(def, from, to, port = null) {
  if (from === to) return def;
  const target = nodeById(def, to);
  if (!target || isTriggerKind(target.kind)) return def;
  if (def.edges.some((e) => e.from === from && e.to === to && (e.port ?? null) === (port ?? null))) return def;
  return { ...def, edges: [...def.edges, { from, to, port: port ?? null }] };
}

export function disconnect(def, from, to, port = null) {
  return { ...def, edges: def.edges.filter((e) => !(e.from === from && e.to === to && (e.port ?? null) === (port ?? null))) };
}

/** Upstream nodes of `id`, nearest last, in an order the reader can follow. */
export function upstreamOf(def, id) {
  const incoming = new Map();
  for (const e of def.edges || []) (incoming.get(e.to) || incoming.set(e.to, []).get(e.to)).push(e.from);
  const seen = new Set([id]);
  const order = [];
  const walk = (cur) => {
    for (const from of incoming.get(cur) || []) {
      if (seen.has(from)) continue;
      seen.add(from);
      walk(from);
      order.push(from);
    }
  };
  walk(id);
  return order.map((nid) => nodeById(def, nid)).filter(Boolean);
}

/**
 * The same key for the same graph, whatever order anything is in.
 *
 * Keys are sorted at every depth, not just at the top: a config that has been
 * to the database and back comes out of jsonb with its keys in another order,
 * and a plain JSON.stringify made an unchanged agent read as "unpublished
 * changes" the moment it was published.
 */
function canonical(v) {
  if (v == null || typeof v !== 'object') return JSON.stringify(v ?? null);
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${canonical(v[k])}`).join(',')}}`;
}

export function stableKey(def) {
  const d = normalize(def);
  const nodes = d.nodes.map((n) => ({ id: n.id, kind: n.kind, name: n.name, config: n.config })).sort((a, b) => a.id.localeCompare(b.id));
  const edges = d.edges.map((e) => `${e.from}>${e.to}>${e.port ?? ''}`).sort();
  return canonical({ nodes, edges });
}

/**
 * What stops this graph running, in the editor's own words. The server is
 * the authority (it validates on save and refuses an invalid publish); this
 * is what the canvas can flag as you build.
 */
export function problemsOf(def) {
  const out = [];
  const trigger = triggerOf(def);
  if (!def.nodes.length) return out;
  if (!trigger) out.push({ code: 'no_trigger', message: 'Add a trigger: what starts this agent.' });
  const triggers = def.nodes.filter((n) => isTriggerKind(n.kind));
  for (const t of triggers.slice(1)) out.push({ code: 'many_triggers', node_id: t.id, message: 'An agent has one trigger.' });
  for (const n of def.nodes) {
    const v = NODE_CATALOG[n.kind]?.validate ? NODE_CATALOG[n.kind].validate(n.config || {}, n) : { ok: true };
    if (v && v.ok === false) out.push({ code: 'config', node_id: n.id, message: v.message });
  }
  for (const e of def.edges) {
    const from = nodeById(def, e.from);
    const ports = portsOf(from);
    if (ports && (e.port == null || !ports.includes(e.port))) {
      out.push({ code: 'port', node_id: e.from, message: `A connection out of "${from.name}" leaves by a branch it no longer has.` });
    }
  }
  if (trigger) {
    const outgoing = new Map();
    for (const e of def.edges) (outgoing.get(e.from) || outgoing.set(e.from, []).get(e.from)).push(e.to);
    const reach = new Set([trigger.id]);
    const stack = [trigger.id];
    while (stack.length) {
      const cur = stack.pop();
      for (const to of outgoing.get(cur) || []) if (!reach.has(to)) { reach.add(to); stack.push(to); }
    }
    for (const n of def.nodes) if (!reach.has(n.id)) out.push({ code: 'unreachable', node_id: n.id, message: `"${n.name}" is not connected to the trigger and will not run.`, warning: true });
  }
  return out;
}
